/**
 * Hover-to-zoom on the product gallery, in the vein of Amazon's magnifier.
 *
 * The image scales up inside its frame and the transform origin follows the
 * cursor, so moving the pointer pans around the magnified shot. All this file
 * does is publish the cursor position as two custom properties; the scaling,
 * the transition and the opt-out live in the stylesheet, so if this script
 * fails to load the gallery is simply a normal gallery.
 *
 * Deliberately skipped:
 *   - Coarse pointers. There is no hover on a touchscreen, and the theme
 *     already opens a full-screen zoom dialog on tap.
 *   - Reduced-motion users, who get no scaling at all.
 */
(() => {
  const MEDIA = '.product-information__media';
  const SLIDE = 'slideshow-slide';

  const canHover =
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const wantsMotion = !window.matchMedia('(prefers-reduced-motion: reduce)')
    .matches;

  if (!canHover || !wantsMotion) return;

  /** Publish cursor position within the slide as percentages. */
  const track = (slide, event) => {
    const box = slide.getBoundingClientRect();
    if (!box.width || !box.height) return;

    const x = ((event.clientX - box.left) / box.width) * 100;
    const y = ((event.clientY - box.top) / box.height) * 100;

    // Clamped so a cursor at the very edge still leaves the frame covered
    // rather than pulling the image away from one side.
    slide.style.setProperty('--rdp-zoom-x', `${Math.min(100, Math.max(0, x))}%`);
    slide.style.setProperty('--rdp-zoom-y', `${Math.min(100, Math.max(0, y))}%`);
  };

  const reset = (slide) => {
    slide.style.removeProperty('--rdp-zoom-x');
    slide.style.removeProperty('--rdp-zoom-y');
    slide.removeAttribute('data-rdp-zooming');
  };

  const bind = (slide) => {
    if (slide.dataset.rdpZoomBound) return;
    slide.dataset.rdpZoomBound = 'true';

    slide.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      slide.setAttribute('data-rdp-zooming', 'true');
      track(slide, event);
    });

    // passive: the handler only writes custom properties, never preventDefault,
    // so the browser can keep scrolling off the main thread.
    slide.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        if (!slide.hasAttribute('data-rdp-zooming')) return;
        track(slide, event);
      },
      { passive: true }
    );

    slide.addEventListener('pointerleave', () => reset(slide));
    // A drag that ends outside the slide never fires pointerleave.
    slide.addEventListener('pointercancel', () => reset(slide));
  };

  const scan = (root) => {
    root.querySelectorAll(`${MEDIA} ${SLIDE}`).forEach(bind);
  };

  scan(document);

  // Slides are replaced when a variant changes or a section re-renders, so new
  // ones have to be picked up rather than bound once at load.
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(SLIDE)) {
          if (node.closest(MEDIA)) bind(node);
        } else {
          scan(node);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
